import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	assignRollToRollset,
	deleteRoll,
	getBarrelLayouts,
	getBarrels,
	getCostCenters,
	getMillSizes,
	getRollApplications,
	getRollChangeReasons,
	getRollMaterials,
	getRollsForAssign,
	getRollsList,
	getRollsetsList,
	getRollsetStatus,
	getStands,
	getSuppliers,
	removeRollFromRollset,
	setRollsetStatus,
	upsertRoll,
	upsertRollset,
	type AssignRollPayload,
	type RemoveRollPayload,
	type RollUpsertPayload,
	type RollsetUpsertPayload,
} from "../repositories/RollshopOperationsRepository";

export const useRollsetsList = () =>
	useQuery({
		queryKey: ["rollshopOperations", "rollsets"],
		queryFn: ({ signal }) => getRollsetsList({ signal }),
	});

export const useRollsList = (
	receiveDateFrom: string | null,
	receiveDateTo: string | null,
) =>
	useQuery({
		// Dates are part of the cache key so changing the toolbar pickers
		// triggers a refetch with the new range.
		queryKey: ["rollshopOperations", "rolls", receiveDateFrom, receiveDateTo],
		queryFn: ({ signal }) =>
			getRollsList({ receiveDateFrom, receiveDateTo, signal }),
	});

// Catalog hooks for filter dropdowns. These read directly from MILL/ROLLS
// (no proxy) — the catalog data is the same in legacy and new (synced via
// RBD.SyncAllCatalogsJob), so no flag bifurcation is needed for these
// auxiliary lookups.
export const useMillSizes = () =>
	useQuery({
		queryKey: ["rollshopOperations", "millSizes"],
		queryFn: ({ signal }) => getMillSizes({ signal }),
		staleTime: 5 * 60 * 1000, // catalog rarely changes; cache for 5 minutes
	});

export const useStands = () =>
	useQuery({
		queryKey: ["rollshopOperations", "stands"],
		queryFn: ({ signal }) => getStands({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useBarrels = () =>
	useQuery({
		queryKey: ["rollshopOperations", "barrels"],
		queryFn: ({ signal }) => getBarrels({ signal }),
		staleTime: 5 * 60 * 1000,
	});

interface MutationOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
}

// Mutation hook for deleting a roll. Invalidates the rolls list on success
// so the grid refetches without a manual refresh.
export const useDeleteRoll = ({ onSuccess, onError }: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) => deleteRoll(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rolls"],
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteRoll: mutation.mutate,
		isDeleting: mutation.isPending,
	};
};

interface UpsertRollOptions {
	onSuccess?: (data: { Id: number }) => void;
	onError?: (error: Error) => void;
}

export const useUpsertRoll = ({
	onSuccess,
	onError,
}: UpsertRollOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (roll: RollUpsertPayload) => upsertRoll(roll),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rolls"],
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertRoll: mutation.mutate,
		isUpserting: mutation.isPending,
	};
};

// Auxiliary catalog hooks. Cached for 5 minutes — these are reference data
// synced from legacy/new every hour, no need to refetch on every render.
export const useRollApplications = () =>
	useQuery({
		queryKey: ["rollshopOperations", "rollApplications"],
		queryFn: ({ signal }) => getRollApplications({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useRollMaterials = () =>
	useQuery({
		queryKey: ["rollshopOperations", "rollMaterials"],
		queryFn: ({ signal }) => getRollMaterials({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useCostCenters = () =>
	useQuery({
		queryKey: ["rollshopOperations", "costCenters"],
		queryFn: ({ signal }) => getCostCenters({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useSuppliers = () =>
	useQuery({
		queryKey: ["rollshopOperations", "suppliers"],
		queryFn: ({ signal }) => getSuppliers({ signal }),
		staleTime: 5 * 60 * 1000,
	});

// =============================================================================
// Rollsets — write mutations + auxiliary lookups
// =============================================================================

interface RollsetMutationOptions {
	onSuccess?: (data?: { Id: number }) => void;
	onError?: (error: Error) => void;
}

export const useUpsertRollset = ({
	onSuccess,
	onError,
}: RollsetMutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (rs: RollsetUpsertPayload) => upsertRollset(rs),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rollsets"],
			});
			onSuccess?.(data);
		},
		onError: (e: Error) => onError?.(e),
	});
	return {
		upsertRollset: mutation.mutate,
		isUpserting: mutation.isPending,
	};
};

export const useAssignRollToRollset = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (p: AssignRollPayload) => assignRollToRollset(p),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rollsets"],
			});
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rolls"],
			});
			onSuccess?.();
		},
		onError: (e: Error) => onError?.(e),
	});
	return {
		assignRoll: mutation.mutate,
		isAssigning: mutation.isPending,
	};
};

export const useRemoveRollFromRollset = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (p: RemoveRollPayload) => removeRollFromRollset(p),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rollsets"],
			});
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rolls"],
			});
			onSuccess?.();
		},
		onError: (e: Error) => onError?.(e),
	});
	return {
		removeRoll: mutation.mutate,
		isRemoving: mutation.isPending,
	};
};

export const useSetRollsetStatus = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: ({ id, statusCode }: { id: number; statusCode: string }) =>
			setRollsetStatus(id, statusCode),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rollsets"],
			});
			onSuccess?.();
		},
		onError: (e: Error) => onError?.(e),
	});
	return {
		setStatus: mutation.mutate,
		isSettingStatus: mutation.isPending,
	};
};

export const useRollsetStatusOptions = () =>
	useQuery({
		queryKey: ["rollshopOperations", "rollsetStatus"],
		queryFn: ({ signal }) => getRollsetStatus({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useRollChangeReasons = (enabled = true) =>
	useQuery({
		queryKey: ["rollshopOperations", "rollChangeReasons"],
		queryFn: ({ signal }) => getRollChangeReasons({ signal }),
		staleTime: 5 * 60 * 1000,
		enabled,
	});

// Fetched on-demand from the assign modal: rolls available to assign,
// filtered by the rollset's mill-size and FREE/SCRAPPED status.
export const useRollsForAssign = (
	millSizeId: number | null,
	enabled: boolean,
) =>
	useQuery({
		queryKey: ["rollshopOperations", "rollsForAssign", millSizeId],
		queryFn: ({ signal }) => getRollsForAssign({ millSizeId, signal }),
		enabled,
	});

// Fetch the layouts (passes) of a barrel — for the Rollset detail panel's
// inline barrel-layout grid. Cached for 5 min like other catalog hooks.
export const useBarrelLayouts = (barrelId: number | null) =>
	useQuery({
		queryKey: ["rollshopOperations", "barrelLayouts", barrelId],
		queryFn: ({ signal }) =>
			getBarrelLayouts({ barrelId: barrelId ?? 0, signal }),
		enabled: barrelId != null && barrelId > 0,
		staleTime: 5 * 60 * 1000,
	});
